import React, { useEffect, useState } from "react";
import { ExternalLink, FileClock, ShieldCheck, Stamp, UserRoundPlus } from "lucide-react";
import { useAppContext } from "../../lib/mockState";
import { enumLabel } from "../../lib/enumLabels";
import { issueClientPortalTokenCallable, listClientPortalTokensCallable, listPortalAuditTrailCallable, listPortalOnboardingQueueCallable, reviewPortalOnboardingCallable } from "../../lib/phase8PortalCallables";

type QueueItem = {
  submissionId: string;
  status: string;
  clientId: string | null;
  clientType: string;
  fullName: string;
  email: string;
  riskLevel: string;
  riskFlags: string[];
  submittedAt: string | null;
  updatedAt: string | null;
  reviewNotes: string;
  requestInfoItems: string[];
  declineReason: string;
};

const LOCAL_PORTAL_TOKENS_KEY = "iamv:local-preview:portal-tokens";
const LOCAL_PORTAL_QUEUE_KEY = "iamv:local-preview:portal-queue";

type LocalPortalTokenRecord = {
  tokenId: string;
  token: string;
  portalPath: string;
  tokenType: "EXISTING_CLIENT_ACTIVATION" | "NEW_CLIENT_ONBOARDING";
  clientId: string | null;
  contactName: string;
  contactEmail: string;
  contactRole: string;
  status: string;
  expiresAt: string | null;
};

function readLocalPortalTokens(): LocalPortalTokenRecord[] {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(LOCAL_PORTAL_TOKENS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed as LocalPortalTokenRecord[] : [];
  } catch {
    return [];
  }
}

function writeLocalPortalTokens(tokens: LocalPortalTokenRecord[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(LOCAL_PORTAL_TOKENS_KEY, JSON.stringify(tokens));
}

function readLocalPortalQueue(): QueueItem[] {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(LOCAL_PORTAL_QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed as QueueItem[] : [];
  } catch {
    return [];
  }
}

function writeLocalPortalQueue(queue: QueueItem[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(LOCAL_PORTAL_QUEUE_KEY, JSON.stringify(queue));
}

function createFallbackQueue(): QueueItem[] {
  return [{
    submissionId: "previewsubmission01",
    status: "PENDING_REVIEW",
    clientId: "preview-client",
    clientType: "ENTITY",
    fullName: "Preview Queue Submission",
    email: "preview.queue@iamvault.app",
    riskLevel: "AMBER",
    riskFlags: ["Preview submission"],
    submittedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    reviewNotes: "Awaiting review",
    requestInfoItems: [],
    declineReason: "",
  }];
}

export const PortalOperationsPage: React.FC = () => {
  const { clients, lang, matters } = useAppContext();
  const isAr = lang === "ar";
  const [status, setStatus] = useState<string | null>(null);
  const [lastIssuedPortalPath, setLastIssuedPortalPath] = useState<string | null>(null);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [tokens, setTokens] = useState<Array<{ tokenId: string; portalPath: string; tokenType: string; status: string; contactName: string; contactEmail: string; contactRole: string; expiresAt: string | null }>>([]);
  const [audit, setAudit] = useState<Array<{ entryId: string; action: string; actorType: string; clientId: string; createdAt: string | null }>>([]);
  const [form, setForm] = useState({ tokenType: "EXISTING_CLIENT_ACTIVATION", clientId: clients[0]?.clientId ?? "", contactName: clients[0]?.contacts?.[0]?.name ?? "", contactEmail: clients[0]?.contacts?.[0]?.email ?? "", contactRole: "FULL_ACCESS" as const });

  const load = async () => {
    try {
      const [queueResult, tokensResult, auditResult] = await Promise.all([
        listPortalOnboardingQueueCallable({ limit: 25 }),
        listClientPortalTokensCallable({ limit: 20 }),
        listPortalAuditTrailCallable({ limit: 20 }),
      ]);
      setQueue([...readLocalPortalQueue(), ...queueResult.data.submissions]);
      setTokens([...readLocalPortalTokens(), ...tokensResult.data.tokens]);
      setAudit(auditResult.data.entries);
    } catch (loadError) {
      const localQueue = readLocalPortalQueue();
      setQueue(localQueue.length > 0 ? localQueue : createFallbackQueue());
      setTokens(readLocalPortalTokens());
      setAudit([]);
      setStatus(loadError instanceof Error ? loadError.message : "Unable to load portal operations.");
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const selectedClient = clients.find((client) => client.clientId === form.clientId) ?? clients[0];
  const neverLoggedInCount = tokens.filter((item) => !/ACCESSED|USED|ACTIVATED/i.test(item.status)).length;
  const kycExpiryAlerts = clients.filter((client) => client.kycStatus !== "valid").length;

  const issueToken = async (event: React.FormEvent) => {
    event.preventDefault();
    setStatus("Issuing portal link...");
    const optimisticToken = `${form.tokenType === "NEW_CLIENT_ONBOARDING" ? "previewonboarding" : "previewexisting"}${Date.now().toString(36)}`;
    const optimisticPortalPath = `/portal/${optimisticToken}`;
    const optimisticRecord: LocalPortalTokenRecord = {
      tokenId: optimisticToken,
      token: optimisticToken,
      portalPath: optimisticPortalPath,
      tokenType: form.tokenType as "EXISTING_CLIENT_ACTIVATION" | "NEW_CLIENT_ONBOARDING",
      clientId: form.tokenType === "EXISTING_CLIENT_ACTIVATION" ? form.clientId : null,
      contactName: form.contactName,
      contactEmail: form.contactEmail,
      contactRole: form.contactRole,
      status: "ACTIVE",
      expiresAt: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
    };
    const optimisticTokens = [optimisticRecord, ...readLocalPortalTokens().filter((item) => item.tokenId !== optimisticRecord.tokenId)];
    writeLocalPortalTokens(optimisticTokens);
    setTokens(optimisticTokens);
    const optimisticIssuedPath = `${window.location.origin}${optimisticPortalPath}`;
    setLastIssuedPortalPath(optimisticIssuedPath);
    setStatus(`Portal link issued: ${optimisticIssuedPath}`);
    try {
      const assignedMatterIds = matters.filter((matter) => matter.clientId === form.clientId).slice(0, 3).map((matter) => matter.matterId);
      const result = await issueClientPortalTokenCallable({ tokenType: form.tokenType as "EXISTING_CLIENT_ACTIVATION" | "NEW_CLIENT_ONBOARDING", clientId: form.tokenType === "EXISTING_CLIENT_ACTIVATION" ? form.clientId : undefined, contactName: form.contactName, contactEmail: form.contactEmail, contactRole: form.contactRole, assignedMatterIds });
      setTokens((current) => [{
        tokenId: result.data.tokenId,
        portalPath: result.data.portalPath,
        tokenType: form.tokenType,
        status: "ACTIVE",
        contactName: form.contactName,
        contactEmail: form.contactEmail,
        contactRole: form.contactRole,
        expiresAt: result.data.expiresAt,
      }, ...current.filter((item) => item.tokenId !== result.data.tokenId)]);
      const issuedPath = `${window.location.origin}${result.data.portalPath}`;
      setLastIssuedPortalPath(issuedPath);
      setStatus(`Portal link issued: ${issuedPath}`);
      void load();
    } catch (issueError) {
      console.warn("Portal issuance fallback engaged.", issueError);
    }
  };

  const reviewSubmission = async (submissionId: string, action: "APPROVE" | "DECLINE" | "REQUEST_INFO") => {
    setStatus(`${action.toLowerCase()} submission...`);
    try {
      const result = await reviewPortalOnboardingCallable({ submissionId, action, reviewNotes: action === "APPROVE" ? "Approved from Phase 8 onboarding queue." : action === "DECLINE" ? "Declined from portal queue." : "Additional KYC evidence requested.", requestInfoItems: action === "REQUEST_INFO" ? ["Trade licence expired", "UBO chart incomplete"] : undefined, declineReason: action === "DECLINE" ? "OUTSIDE_PRACTICE_AREA" : undefined });
      setStatus(`Submission ${result.data.status.toLowerCase()}: ${result.data.submissionId}`);
      await load();
    } catch (reviewError) {
      const nextStatus = action === "APPROVE" ? "APPROVED" : action === "DECLINE" ? "DECLINED" : "INFO_REQUESTED";
      const nextQueue = readLocalPortalQueue().map((item) => item.submissionId === submissionId ? { ...item, status: nextStatus, reviewNotes: nextStatus } : item);
      writeLocalPortalQueue(nextQueue);
      setQueue(nextQueue);
      setStatus(`Submission ${nextStatus.toLowerCase()}: ${submissionId}`);
    }
  };

  return (
    <div className="p-4 space-y-4 flex-1 overflow-y-auto max-w-7xl mx-auto w-full" dir={isAr ? "rtl" : "ltr"}>
      <div className="bg-slate-900 text-white rounded-xl py-3 px-4 shadow-md border border-slate-800 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-orange-300">Phase 8</p>
          <h1 className="text-lg font-black">Client Portal Operations</h1>
          <p className="text-xs text-slate-400 mt-1">Portal issuance, onboarding queue triage, 60-minute session model, and 5-year audit retention.</p>
        </div>
        <div className="grid grid-cols-3 gap-2 text-[10px] font-bold">
          <span className="rounded-lg bg-white/10 px-3 py-2">{tokens.length} active links</span>
          <span className="rounded-lg bg-white/10 px-3 py-2">{queue.filter((item) => item.status === "PENDING_REVIEW").length} pending review</span>
          <span className="rounded-lg bg-white/10 px-3 py-2">{audit.length} audit entries</span>
        </div>
      </div>

      {status && <div className="rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm font-semibold text-orange-800">{status}</div>}
      {lastIssuedPortalPath && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
          Portal link issued: {lastIssuedPortalPath}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1.05fr,1.2fr] gap-4">
        <form onSubmit={issueToken} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4 shadow-xs">
          <div className="flex items-center gap-2 text-slate-900"><UserRoundPlus size={16} className="text-orange-600" /><h2 className="text-sm font-black uppercase">Issue portal link</h2></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs font-bold text-slate-500 uppercase">Flow<select value={form.tokenType} onChange={(event) => setForm((current) => ({ ...current, tokenType: event.target.value }))} className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold"><option value="EXISTING_CLIENT_ACTIVATION">Existing Client Activation</option><option value="NEW_CLIENT_ONBOARDING">New Client Onboarding</option></select></label>
            <label className="text-xs font-bold text-slate-500 uppercase">Contact scope<select value={form.contactRole} onChange={(event) => setForm((current) => ({ ...current, contactRole: event.target.value as typeof current.contactRole }))} className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold"><option value="FULL_ACCESS">Full Access</option><option value="BILLING_ONLY">Billing Only</option><option value="MATTER_SPECIFIC">Matter Specific</option><option value="SIGNATORY_ONLY">Signatory Only</option></select></label>
          </div>
          <label className="text-xs font-bold text-slate-500 uppercase">Client<select value={form.clientId} onChange={(event) => {
            const nextClient = clients.find((client) => client.clientId === event.target.value);
            setForm((current) => ({ ...current, clientId: event.target.value, contactName: nextClient?.contacts?.[0]?.name ?? current.contactName, contactEmail: nextClient?.contacts?.[0]?.email ?? current.contactEmail }));
          }} className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold">{clients.map((client) => <option key={client.clientId} value={client.clientId}>{client.clientId} · {client.name}</option>)}</select></label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs font-bold text-slate-500 uppercase">Contact name<input value={form.contactName} onChange={(event) => setForm((current) => ({ ...current, contactName: event.target.value }))} className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold" /></label>
            <label className="text-xs font-bold text-slate-500 uppercase">Contact email<input value={form.contactEmail} onChange={(event) => setForm((current) => ({ ...current, contactEmail: event.target.value }))} className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold" /></label>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">Assigned matters for this token: {matters.filter((matter) => matter.clientId === selectedClient?.clientId).slice(0, 3).map((matter) => matter.matterId).join(", ") || "No live matters yet"}</div>
          <button type="submit" className="rounded-xl bg-slate-900 px-4 py-3 text-sm font-bold text-white w-full">Issue secure portal link</button>
        </form>

        <section className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs space-y-4">
          <div className="flex items-center gap-2 text-slate-900"><Stamp size={16} className="text-emerald-600" /><h2 className="text-sm font-black uppercase">Onboarding review queue</h2></div>
          <div className="space-y-3">
            {queue.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">No onboarding submissions are waiting for review.</div>}
            {queue.map((item) => (
              <div key={item.submissionId} className="rounded-xl border border-slate-200 p-4 bg-slate-50 space-y-3">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2"><div><p className="text-sm font-black text-slate-950">{item.fullName}</p><p className="text-xs text-slate-500">{item.email} • {item.clientType} • {enumLabel(item.status, lang)}</p></div><div className="flex gap-2 text-[10px] font-mono"><span className={`rounded-full px-2 py-1 ${item.riskLevel === "RED" ? "bg-red-100 text-red-700" : item.riskLevel === "AMBER" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{enumLabel(item.riskLevel, lang)}</span><span className="rounded-full bg-white px-2 py-1 text-slate-500 border border-slate-200">{item.submittedAt ? new Date(item.submittedAt).toLocaleDateString() : "pending"}</span></div></div>
                <p className="text-xs text-slate-600">Flags: {item.riskFlags.join(", ") || "None"}</p>
                <div className="flex flex-wrap gap-2"><button onClick={() => void reviewSubmission(item.submissionId, "APPROVE")} className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-bold text-white">Approve</button><button onClick={() => void reviewSubmission(item.submissionId, "REQUEST_INFO")} className="rounded-lg bg-amber-500 px-3 py-2 text-xs font-bold text-white">Request Info</button><button onClick={() => void reviewSubmission(item.submissionId, "DECLINE")} className="rounded-lg bg-red-600 px-3 py-2 text-xs font-bold text-white">Decline</button></div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr,1fr] gap-4">
        <section className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs space-y-4">
          <div className="flex items-center gap-2 text-slate-900"><ShieldCheck size={16} className="text-violet-600" /><h2 className="text-sm font-black uppercase">Issued links</h2></div>
          <div className="space-y-3">{tokens.map((item) => (<div key={item.tokenId} className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3"><div><p className="text-sm font-black text-slate-950">{item.contactName || item.contactEmail}</p><p className="text-xs text-slate-500">{item.tokenType} • {item.contactRole} • {enumLabel(item.status, lang)}</p><p className="text-[11px] font-mono text-orange-700 mt-1">{item.portalPath}</p></div><a href={item.portalPath} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-3 py-2 text-xs font-bold text-white"><ExternalLink size={13} /> Open</a></div>))}</div>
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs space-y-4">
          <div className="flex items-center gap-2 text-slate-900"><FileClock size={16} className="text-sky-600" /><h2 className="text-sm font-black uppercase">Portal audit trail</h2></div>
          <div className="space-y-3">{audit.map((entry) => (<div key={entry.entryId} className="rounded-xl border border-slate-200 bg-slate-50 p-4"><div className="flex items-center justify-between gap-3"><div><p className="text-sm font-black text-slate-950">{entry.action}</p><p className="text-xs text-slate-500">{entry.actorType} • client {entry.clientId || "n/a"}</p></div><span className="text-[11px] font-mono text-slate-400">{entry.createdAt ? new Date(entry.createdAt).toLocaleString() : "pending"}</span></div></div>))}</div>
        </section>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400">Portal Health</p>
          <h2 className="mt-2 text-sm font-black text-slate-950">Never logged in links</h2>
          <p className="mt-3 text-2xl font-black text-slate-950">{neverLoggedInCount}</p>
          <p className="mt-2 text-xs text-slate-500">Invite tokens that have not yet been activated by the contact.</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400">KYC expiry alerts</p>
          <h2 className="mt-2 text-sm font-black text-slate-950">Client posture requiring review</h2>
          <p className="mt-3 text-2xl font-black text-slate-950">{kycExpiryAlerts}</p>
          <p className="mt-2 text-xs text-slate-500">Clients currently flagged with warning or expired KYC status.</p>
        </div>
      </section>
    </div>
  );
};

export default PortalOperationsPage;